import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Briefcase, DollarSign, TrendingUp, Calendar } from 'lucide-react';
import { Project, TacticalSpecialist, PerformanceKicker } from '@/types/project';
import { usePermissions } from '@/hooks/usePermissions';

interface PersonalDashboardProps {
  projects: Project[];
  onSelectProject?: (project: Project) => void;
}

interface PersonalProjectEarnings {
  project: Project;
  tacticalEarnings: number;
  kickerEarnings: number;
  operationsEarnings: number;
  total: number;
  roles: string[];
}

// Kicker tier percentages for calculations
const KICKER_TIERS: Record<string, number> = {
  'tier1': 0.03,
  'tier2': 0.05,
  'tier3': 0.07,
  'tier4': 0.10,
  'none': 0,
};

export function PersonalDashboard({ projects, onSelectProject }: PersonalDashboardProps) {
  const { userProfile, isExternalPartner, isTeamMember } = usePermissions();

  const displayName = userProfile?.display_name || '';

  // Calculate personal earnings from assigned projects
  const personalData = useMemo(() => {
    if (!displayName) {
      return {
        assignedProjects: [],
        totalProjected: 0,
        totalTactical: 0,
        totalKickers: 0,
        totalOperations: 0,
      };
    }

    const assignedProjects: PersonalProjectEarnings[] = [];
    let totalTactical = 0;
    let totalKickers = 0;
    let totalOperations = 0;

    // Only include active projects
    const activeProjects = projects.filter(p => p.status === 'active');

    activeProjects.forEach(project => {
      const base = project.baseTemplate;
      let projectTactical = 0;
      let projectKickers = 0;
      let projectOperations = 0;
      const roles: string[] = [];

      // Check if user is assigned as a tactical specialist
      base.tacticalSpecialists.forEach(specialist => {
        if (specialist.assignedOwner?.toLowerCase() === displayName.toLowerCase()) {
          projectTactical += specialist.amount;
          roles.push(specialist.name);
        }
      });

      // Check if user is eligible for kickers
      base.performanceKickers.forEach(kicker => {
        if (kicker.eligiblePeople) {
          const isEligible = kicker.eligiblePeople.some(
            p => p.name.toLowerCase() === displayName.toLowerCase()
          );
          if (isEligible && kicker.selectedTier && kicker.selectedTier !== 'none') {
            const tierPercentage = KICKER_TIERS[kicker.selectedTier] || 0;
            const kickerRevenue = kicker.marketingSpend * tierPercentage;
            const sharePercentage = kicker.sharePercentage || 10;
            const eligibleCount = kicker.eligiblePeople.length;
            const personalShare = (kickerRevenue * (sharePercentage / 100)) / eligibleCount;
            projectKickers += personalShare;
            if (!roles.includes(`Kicker: ${kicker.name}`)) {
              roles.push(`Kicker: ${kicker.name}`);
            }
          }
        }
      });

      // Check if user is an operations owner (only for team members, not external)
      if (!isExternalPartner && base.operationsOwners.some(
        owner => owner.toLowerCase() === displayName.toLowerCase()
      )) {
        projectOperations += base.operationsCompensationPerOwner;
        if (!roles.includes('Operations')) {
          roles.push('Operations');
        }
      }

      // Only add project if user has any earnings from it
      if (projectTactical > 0 || projectKickers > 0 || projectOperations > 0) {
        assignedProjects.push({
          project,
          tacticalEarnings: projectTactical,
          kickerEarnings: projectKickers,
          operationsEarnings: projectOperations,
          total: projectTactical + projectKickers + projectOperations,
          roles,
        });
        totalTactical += projectTactical;
        totalKickers += projectKickers;
        totalOperations += projectOperations;
      }
    });

    return {
      assignedProjects,
      totalProjected: totalTactical + totalKickers + totalOperations,
      totalTactical,
      totalKickers,
      totalOperations,
    };
  }, [projects, displayName, isExternalPartner]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (!displayName) {
    return (
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6 overflow-auto">
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Please update your profile with a display name to see your earnings.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6 overflow-auto">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-semibold font-serif tracking-tight">My Dashboard</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Welcome back, {displayName}
        </p>
      </div>

      {/* Earnings Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 uppercase tracking-wider text-xs">
              <DollarSign className="h-4 w-4" />
              Projected Monthly
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-serif text-positive">
              {formatCurrency(personalData.totalProjected)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 uppercase tracking-wider text-xs">
              <Briefcase className="h-4 w-4" />
              Active Projects
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-serif">
              {personalData.assignedProjects.length}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 uppercase tracking-wider text-xs">
              <TrendingUp className="h-4 w-4" />
              Tactical Work
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-serif">
              {formatCurrency(personalData.totalTactical)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 uppercase tracking-wider text-xs">
              <Calendar className="h-4 w-4" />
              Kicker Bonuses
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-serif text-info">
              {formatCurrency(personalData.totalKickers)}
            </p>
            <p className="text-xs text-muted-foreground">Projected</p>
          </CardContent>
        </Card>
      </div>

      {/* My Projects */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-serif">My Assigned Projects</CardTitle>
          <CardDescription>
            Projects where you have active compensation
          </CardDescription>
        </CardHeader>
        <CardContent>
          {personalData.assignedProjects.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              You are not currently assigned to any active projects.
            </p>
          ) : (
            <div className="space-y-4">
              {personalData.assignedProjects.map(({ project, tacticalEarnings, kickerEarnings, operationsEarnings, total, roles }) => (
                <div
                  key={project.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border hover:bg-accent/50 cursor-pointer transition-colors"
                  onClick={() => onSelectProject?.(project)}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{project.projectName}</h4>
                      <Badge variant="outline" className="text-xs">
                        {project.status}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {roles.map(role => (
                        <Badge key={role} variant="secondary" className="text-xs">
                          {role}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="mt-2 sm:mt-0 sm:text-right">
                    <p className="text-lg font-semibold font-serif text-positive">
                      {formatCurrency(total)}/mo
                    </p>
                    <div className="text-xs text-muted-foreground space-x-2">
                      {tacticalEarnings > 0 && (
                        <span>Tactical: {formatCurrency(tacticalEarnings)}</span>
                      )}
                      {kickerEarnings > 0 && (
                        <span>Kickers: {formatCurrency(kickerEarnings)}</span>
                      )}
                      {operationsEarnings > 0 && (
                        <span>Ops: {formatCurrency(operationsEarnings)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Earnings Breakdown */}
      {personalData.totalProjected > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-serif">Monthly Earnings Breakdown</CardTitle>
            <CardDescription>
              How your projected earnings are calculated
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {personalData.totalTactical > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Tactical Compensation</span>
                  <span className="font-medium">{formatCurrency(personalData.totalTactical)}</span>
                </div>
              )}
              {personalData.totalOperations > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Operations Compensation</span>
                  <span className="font-medium">{formatCurrency(personalData.totalOperations)}</span>
                </div>
              )}
              {personalData.totalKickers > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Performance Kickers (Projected)</span>
                  <span className="font-medium text-info">{formatCurrency(personalData.totalKickers)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between items-center">
                <span className="font-medium">Total Monthly</span>
                <span className="font-bold font-serif text-positive text-lg">
                  {formatCurrency(personalData.totalProjected)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
